import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Drawer, Descriptions, Tabs, Table, Button, Tag, message, Empty, Image } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { PickingTaskDTO, PickingTaskDetailDTO, PickingResultItemDTO, QueryPlanDetailsRequest, QueryPickingResultsRequest } from '../../../../types/picking/pickingTask';
import type { Paging } from '../../../../types/common';
import * as pickingTaskService from '../../../../services/picking/pickingTaskService';
import {
  ShortageReasonOptions,
  CancelReasonOptions,
  TaskSourceOptions,
  PickingTaskStatusOptions,
} from '../../../../constants/picking';

interface PickingTaskDetailProps {
  visible: boolean;
  taskCode: string | null;
  onClose: () => void;
}

// 抽屉宽度限制
const MIN_DRAWER_WIDTH = 400;
const getDefaultDrawerWidth = () => Math.floor(window.innerWidth / 2);

const PickingTaskDetail: React.FC<PickingTaskDetailProps> = ({ visible, taskCode, onClose }) => {
  const { t } = useTranslation();
  const [taskDetail, setTaskDetail] = useState<PickingTaskDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('plan');

  // 抽屉宽度（默认占屏幕一半，支持左侧边缘拖拽调整）
  const [drawerWidth, setDrawerWidth] = useState<number>(getDefaultDrawerWidth);
  const isResizingRef = useRef(false);

  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;
    const newWidth = window.innerWidth - e.clientX;
    const maxWidth = window.innerWidth - 200;
    setDrawerWidth(Math.min(Math.max(newWidth, MIN_DRAWER_WIDTH), maxWidth));
  }, []);

  const handleResizeMouseUp = useCallback(() => {
    isResizingRef.current = false;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    document.removeEventListener('mousemove', handleResizeMouseMove);
    document.removeEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', handleResizeMouseMove);
    document.addEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  // 卸载时清理事件监听
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  // 拣选计划 Tab 状态
  const [planData, setPlanData] = useState<PickingTaskDetailDTO[]>([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [planPagination, setPlanPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });

  // 拣货结果 Tab 状态
  const [resultData, setResultData] = useState<PickingResultItemDTO[]>([]);
  const [resultLoading, setResultLoading] = useState(false);
  const [resultPagination, setResultPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });

  // 加载任务详情
  const loadTaskDetail = async (code: string) => {
    setLoading(true);
    try {
      const response = await pickingTaskService.getPickingTaskByCode(code);
      if (response.success && response.data) {
        setTaskDetail(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 加载拣选计划明细
  const loadPlanDetails = async (code: string, params: QueryPlanDetailsRequest) => {
    setPlanLoading(true);
    try {
      const response = await pickingTaskService.pagePlanDetails(code, params);
      if (response.success && response.data) {
        setPlanData(response.data.tableData);
        setPlanPagination(prev => ({
          ...response.data.paging,
          pageSize: params.pageSize ?? prev.pageSize,
        }));
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setPlanLoading(false);
    }
  };

  // 加载拣货结果
  const loadPickingResults = async (code: string, params: QueryPickingResultsRequest) => {
    setResultLoading(true);
    try {
      const response = await pickingTaskService.pagePickingResults(code, params);
      if (response.success && response.data) {
        setResultData(response.data.tableData);
        setResultPagination(prev => ({
          ...response.data.paging,
          pageSize: params.pageSize ?? prev.pageSize,
        }));
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setResultLoading(false);
    }
  };

  // Drawer 打开时加载数据
  useEffect(() => {
    if (visible && taskCode) {
      loadTaskDetail(taskCode);
      if (activeTab === 'plan') {
        loadPlanDetails(taskCode, { currentPage: 1, pageSize: 10 });
      } else {
        loadPickingResults(taskCode, { currentPage: 1, pageSize: 10 });
      }
    }
  }, [visible, taskCode]);

  // Tab 切换时加载对应数据
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (taskCode) {
      if (key === 'plan') {
        loadPlanDetails(taskCode, { currentPage: 1, pageSize: 10 });
      } else {
        loadPickingResults(taskCode, { currentPage: 1, pageSize: 10 });
      }
    }
  };

  // 拣选计划分页变化
  const handlePlanPageChange = (page: number, pageSize: number) => {
    setPlanPagination(prev => ({ ...prev, currentPage: page, pageSize }));
    if (taskCode) {
      loadPlanDetails(taskCode, {
        currentPage: page,
        pageSize,
      });
    }
  };

  // 拣货结果分页变化
  const handleResultPageChange = (page: number, pageSize: number) => {
    setResultPagination(prev => ({ ...prev, currentPage: page, pageSize }));
    if (taskCode) {
      loadPickingResults(taskCode, {
        currentPage: page,
        pageSize,
      });
    }
  };

  // 格式化时间
  const formatTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('DD-MM-YYYY HH:mm:ss') : '-';
  };

  // 渲染状态Tag
  const renderStatusTag = (status: string, options: any[]) => {
    const option = options.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  // 拣选计划表格列
  const planColumns = [
    {
      title: t('picking.pickingTask.detail.plan.table.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
    },
    {
      title: t('picking.pickingTask.detail.plan.table.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 150,
    },
    {
      title: t('picking.pickingTask.detail.plan.table.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200,
    },
    {
      title: t('picking.pickingTask.detail.plan.table.image'),
      dataIndex: 'productImage',
      key: 'productImage',
      width: 80,
      render: (url: string | null) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
    {
      title: t('picking.pickingTask.detail.plan.table.planQuantity'),
      dataIndex: 'planQuantity',
      key: 'planQuantity',
      width: 100,
    },
    {
      title: t('picking.pickingTask.detail.plan.table.weighted'),
      dataIndex: 'isWeighted',
      key: 'isWeighted',
      width: 80,
      render: (val: boolean | null) => val ? <Tag color="blue">{t('common.yes')}</Tag> : t('common.no'),
    },
    {
      title: t('picking.pickingTask.detail.plan.table.freebie'),
      dataIndex: 'isFreebie',
      key: 'isFreebie',
      width: 80,
      render: (val: boolean | null) => val ? <Tag color="gold">{t('common.yes')}</Tag> : t('common.no'),
    },
    {
      title: t('picking.pickingTask.detail.plan.table.promotion'),
      dataIndex: 'isPromotion',
      key: 'isPromotion',
      width: 80,
      render: (val: boolean | null) => val ? <Tag color="green">{t('common.yes')}</Tag> : t('common.no'),
    },
    {
      title: t('picking.pickingTask.detail.plan.table.promotionType'),
      dataIndex: 'promotionType',
      key: 'promotionType',
      width: 120,
      render: (text: string | null) => text || '-',
    },
    {
      title: t('picking.pickingTask.detail.plan.table.locationCode'),
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 120,
      render: (text: string | null) => text || '-',
    },
  ];

  // 拣货结果表格列
  const resultColumns = [
    {
      title: t('picking.pickingTask.detail.result.table.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
    },
    {
      title: t('picking.pickingTask.detail.result.table.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 150,
    },
    {
      title: t('picking.pickingTask.detail.result.table.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200,
      render: (text: string, record: PickingResultItemDTO) => (
        <>
          {record.relatedDetailId && <Tag color="orange">{t('picking.pickingTask.detail.result.substituteTag')}</Tag>}
          {text}
        </>
      ),
    },
    {
      title: t('picking.pickingTask.detail.result.table.image'),
      dataIndex: 'productImage',
      key: 'productImage',
      width: 80,
      render: (url: string | null) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
    {
      title: t('picking.pickingTask.detail.result.table.planQuantity'),
      dataIndex: 'planQuantity',
      key: 'planQuantity',
      width: 100,
    },
    {
      title: t('picking.pickingTask.detail.result.table.actualQuantity'),
      dataIndex: 'actualQuantity',
      key: 'actualQuantity',
      width: 100,
    },
    {
      title: t('picking.pickingTask.detail.result.table.weighted'),
      dataIndex: 'isWeighted',
      key: 'isWeighted',
      width: 80,
      render: (val: boolean | null) => val ? <Tag color="blue">{t('common.yes')}</Tag> : t('common.no'),
    },
    {
      title: t('picking.pickingTask.detail.result.table.freebie'),
      dataIndex: 'isFreebie',
      key: 'isFreebie',
      width: 80,
      render: (val: boolean | null) => val ? <Tag color="gold">{t('common.yes')}</Tag> : t('common.no'),
    },
    {
      title: t('picking.pickingTask.detail.result.table.promotion'),
      dataIndex: 'isPromotion',
      key: 'isPromotion',
      width: 80,
      render: (val: boolean | null) => val ? <Tag color="green">{t('common.yes')}</Tag> : t('common.no'),
    },
    {
      title: t('picking.pickingTask.detail.result.table.promotionType'),
      dataIndex: 'promotionType',
      key: 'promotionType',
      width: 120,
      render: (text: string | null) => text || '-',
    },
    {
      title: t('picking.pickingTask.detail.result.table.isSubstituted'),
      dataIndex: 'isSubstituted',
      key: 'isSubstituted',
      width: 100,
      render: (isSubstituted: boolean) => (
        <Tag color={isSubstituted ? 'orange' : 'default'}>
          {isSubstituted ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('picking.pickingTask.detail.result.table.isRepick'),
      dataIndex: 'isRepick',
      key: 'isRepick',
      width: 100,
      render: (isRepick: boolean) => (
        <Tag color={isRepick ? 'blue' : 'default'}>
          {isRepick ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('picking.pickingTask.detail.result.table.isCancel'),
      dataIndex: 'isCancel',
      key: 'isCancel',
      width: 100,
      render: (isCancel: boolean) => (
        <Tag color={isCancel ? 'red' : 'default'}>
          {isCancel ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('picking.pickingTask.detail.result.table.isShortage'),
      dataIndex: 'isShortage',
      key: 'isShortage',
      width: 100,
      render: (isShortage: boolean) => (
        <Tag color={isShortage ? 'orange' : 'default'}>
          {isShortage ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('picking.pickingTask.detail.result.table.shortageReason'),
      dataIndex: 'shortageReason',
      key: 'shortageReason',
      width: 120,
      render: (reason: string | null) => {
        if (!reason) return '-';
        const option = ShortageReasonOptions.find(opt => opt.value === reason);
        return option ? t(option.label) : reason;
      },
    },
    {
      title: t('picking.pickingTask.detail.result.table.cancelReason'),
      dataIndex: 'cancelReason',
      key: 'cancelReason',
      width: 120,
      render: (reason: string | null) => {
        if (!reason) return '-';
        const option = CancelReasonOptions.find(opt => opt.value === reason);
        return option ? t(option.label) : reason;
      },
    },
    {
      title: t('picking.pickingTask.detail.result.table.pickerName'),
      dataIndex: 'pickerName',
      key: 'pickerName',
      width: 120,
      render: (text: string | null) => text || '-',
    },
    {
      title: t('picking.pickingTask.detail.result.table.pickingTime'),
      dataIndex: 'pickingTime',
      key: 'pickingTime',
      width: 180,
      render: formatTime,
    },
  ];

  return (
    <Drawer
      title={t('picking.pickingTask.detail.title')}
      width={drawerWidth}
      open={visible}
      onClose={onClose}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onClose}>{t('common.actions.close')}</Button>
        </div>
      }
    >
      {/* 左侧拖拽手柄：调整抽屉宽度 */}
      <div
        onMouseDown={handleResizeMouseDown}
        style={{
          position: 'fixed',
          top: 0,
          right: drawerWidth - 3,
          width: 6,
          height: '100vh',
          cursor: 'col-resize',
          zIndex: 1050,
        }}
      />
      {taskDetail && (
        <>
          <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.taskCode')}>
              {taskDetail.taskCode}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.waveNo')}>
              {taskDetail.waveNo || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.taskSource')}>
              {t(TaskSourceOptions.find(opt => opt.value === taskDetail.taskSource)?.label || '')}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.taskStatus')}>
              {renderStatusTag(taskDetail.taskStatus, PickingTaskStatusOptions)}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.orderCount')}>
              {taskDetail.orderCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.skuCount')}>
              {taskDetail.skuCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.itemCount')}>
              {taskDetail.itemCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.pickedSkuCount')}>
              {taskDetail.pickedSkuCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.pickedItemCount')}>
              {taskDetail.pickedItemCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.shortageSkuCount')}>
              <span style={{ color: taskDetail.shortageSkuCount > 0 ? 'red' : 'inherit' }}>
                {taskDetail.shortageSkuCount}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.shortageItemCount')}>
              <span style={{ color: taskDetail.shortageItemCount > 0 ? 'red' : 'inherit' }}>
                {taskDetail.shortageItemCount}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.pickerName')}>
              {taskDetail.pickerName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.storeName')}>
              {taskDetail.storeName}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.startTime')}>
              {formatTime(taskDetail.startTime)}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.finishTime')}>
              {formatTime(taskDetail.finishTime)}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.totalDuration')}>
              {taskDetail.totalDuration || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('picking.pickingTask.detail.basic.createTime')}>
              {formatTime(taskDetail.gmtCreate)}
            </Descriptions.Item>
          </Descriptions>

          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <Tabs.TabPane tab={t('picking.pickingTask.detail.tabs.planDetails')} key="plan">
              <Table
                columns={planColumns}
                dataSource={planData}
                rowKey="detailId"
                loading={planLoading}
                pagination={{
                  current: planPagination.currentPage,
                  pageSize: planPagination.pageSize,
                  total: planPagination.totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => t('common.pagination.total', { total }),
                  onChange: handlePlanPageChange,
                }}
                scroll={{ x: 1400 }}
              />
            </Tabs.TabPane>

            <Tabs.TabPane tab={t('picking.pickingTask.detail.tabs.pickingResults')} key="result">
              {resultData.length === 0 && !resultLoading ? (
                <Empty description={t('picking.pickingTask.detail.result.empty')} />
              ) : (
                <Table
                  columns={resultColumns}
                  dataSource={resultData}
                  rowKey="detailId"
                  loading={resultLoading}
                  pagination={{
                    current: resultPagination.currentPage,
                    pageSize: resultPagination.pageSize,
                    total: resultPagination.totalCount,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => t('common.pagination.total', { total }),
                    onChange: handleResultPageChange,
                  }}
                  scroll={{ x: 1800 }}
                />
              )}
            </Tabs.TabPane>
          </Tabs>
        </>
      )}
    </Drawer>
  );
};

export default PickingTaskDetail;
